import React, { useCallback, useEffect, useState, useRef } from "react";
import styled from "@emotion/styled";
import BackHeader from "@components/header/BackHeader";
import ProductSwiper from "@components/swiper/ProductSwiper";
import LikeButton from "@components/button/LikeButton";
import ShareButton from "@components/button/ShareButton";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import ColorBall from "@components/item/ColorBall";
import ProductListSwiper from "@components/swiper/ProductListSwiper";
import ExchangeRefundModal from "@components/modal/ExchangeRefundModal";
import ChoiceOptionDrawer from "@components/drawer/ChoiceOptionDrawer";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { useNavigate, useParams } from "react-router-dom";
import SellerInfoCard from "@components/card/SellerInfoCard";
import useProductIdStore from "@store/productDetail/useProductIdStore";
import useUserInfoStore from "@store/useUserInfoStore";
import BasicModal from "@components/modal/BasicModal";
import BasicDrawer from "@components/drawer/BasicDrawer";
import { ModalContent } from "@components/modal/ModalContent";
import useOrderStore from "@store/order/useOrderStore";
import useAxios from "@libs/axios/useAxios";
import { LOAD_SIZE_10, queryClient } from "@libs/reactQuery/queryClient";
import { GET_PRODUCT_URL } from "@libs/apiUrl";
import { useTranslation } from "react-i18next";
import { AdSwiper } from "@pages/home/_components";
import { toPrice } from "@utils/functions";
import { Flex } from "antd";
const Container = styled.div({
  display: "flex",
  flexDirection: "column",
});

const ProductInfoWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  padding: "2rem",
}));

const CategoryBox = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  color: theme.color.gray,
}));

const IconBox = styled.div({
  display: "flex",
  gap: "0.8rem",
});

const BrandWithTitle = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "1rem",
  paddingBottom: "1rem",
  borderBottom: `1px solid ${theme.color.border_gray}`,

  ".brand_name": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.8rem",
  },

  ".brand": {
    fontSize: "2rem",
    fontWeight: theme.fontWeight.semiBold,
    lineHeight: "2rem",
  },
}));

const ConditionBox = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  color: theme.color.gray,

  ".label": {
    minWidth: "5rem",
  },

  ".color_box": {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
  },
}));

const SizeItem = styled.span<{ disabled: boolean }>(({ theme, disabled }) => ({
  padding: "0.7rem",
  color: disabled ? theme.color.light_gray : theme.color.black,
  minHeight: "3rem",
}));

const PriceBox = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "1rem",
  fontWeight: theme.fontWeight.bold,
  fontSize: "1.6rem",
  padding: "1rem 0",

  ".discount": {
    color: theme.color.green,
    fontWeight: theme.fontWeight.semiBold,
    fontSize: "2rem",
  },
}));

const AdWrapper = styled.button(({ theme }) => ({
  width: "100%",
  padding: "2rem",
}));

const ImageItem = styled.div(({ theme }) => ({
  borderRadius: "1rem",
  overflow: "hidden",
  width: "100%",
}));

const LabelWithContent = styled.div<{ last?: boolean }>(({ theme, last }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
  padding: last ? "2rem 2rem 0 2rem" : "2rem",
  lineHeight: "2rem",

  label: {
    display: "flex",
    gap: "0.8rem",
    alignItems: "center",
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.semiBold,
    lineHeight: "2rem",
  },

  ".brand": {
    fontSize: "2rem",
    fontWeight: theme.fontWeight.semiBold,
    lineHeight: "2rem",
  },
}));

const Line = styled.div(({ theme }) => ({
  width: "100%",
  height: "1px",
  backgroundColor: theme.color.border_gray,
  marginTop: "2rem",
}));

const Delivery = styled.span(({ theme }) => ({
  color: theme.color.gray,
  fontWeight: theme.fontWeight.medium,
}));

const DeliveryFeeBox = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "end",
  alignItems: "start",

  gap: "1rem",

  ".value": {
    color: theme.color.black,
  },
}));

const ProductDescription = styled.div(({ theme }) => ({
  width: "100%",
  height: "100%",
  textarea: {
    width: "100%",
    height: "150px",
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    resize: "none",

    ":disabled": {
      backgroundColor: "white",
    },
  },
}));

const PolicyBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.8rem",
  fontWeight: theme.fontWeight.medium,
}));

const EditButtonContainer = styled.div(({ theme }) => ({
  position: "relative",
  width: "100%",
}));

const EditMenuButton = styled.div(({ theme }) => ({
  position: "absolute",
  zIndex: 2,
  width: "100%",

  background: "linear-gradient(0deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)",

  // background: "linear-gradient(#303030, rgba(0, 0, 0, 0.00))",

  height: "50px",
}));

const StyledImage = styled.img(({ theme }) => ({
  display: "flex",
  objectFit: "cover",
  position: "absolute",
  right: "1.6rem",
  top: "1rem",
  cursor: "pointer",
}));

const ItemBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
}));

const Item = styled.button(({ theme }) => ({
  width: "100%",
  minHeight: "5.6rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `1px solid ${theme.color.border_gray}`,

  "&:first-child": {
    border: "none",
  },

  "&:last-child": {
    color: theme.color.orange,
    justifyContent: "center",
  },
}));

const WarningMessage = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  fontSize: "1.4rem",
  color: theme.color.orange,
}));

const ProductDetail = () => {
  const theme = useTheme();
  const [modal, setModal] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const {
    getProductDetail,
    getSizeWithStock,
    postCart,
    updateProductStatus,
    deleteProduct,
    getSellerInfo,
    getMemberSubscription,
    postCopyProduct,
  } = useApiRequest();
  const params = useParams();
  const { setProductId } = useProductIdStore();
  const lang = window.localStorage.getItem("language");
  const { axiosInstance } = useAxios();
  const [deactiveModal, setDeactiveModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const navigate = useNavigate();
  const { userInfo } = useUserInfoStore();
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const { setOrder, setOrderType } = useOrderStore();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: productInfo, isLoading: productInfoLoading } = useQuery({
    queryKey: ["productInfo", params.productId],
    queryFn: () => getProductDetail(Number(params?.productId)),
    enabled: !!params.productId,
  });

  const productStatus = productInfo?.is_active;

  useEffect(() => {
    setSelectedColor(productInfo?.color_list[0].color_id);
  }, [productInfo]);

  useEffect(() => {
    setProductId(Number(params?.productId));
  }, [params?.productId, setProductId]);

  const { data: size } = useQuery({
    queryKey: ["size", productInfo?.product_id, selectedColor],
    queryFn: () => getSizeWithStock(productInfo?.product_id, selectedColor),
    enabled: !!productInfo?.product_id && !!selectedColor,
  });

  const { mutate: updateProductStatusMutate } = useMutation({
    mutationFn: (data: any) =>
      updateProductStatus(Number(params.productId), data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["productList"],
      });
      navigate(-1);
    },
  });

  const { mutate: deleteProductMutate } = useMutation({
    mutationFn: () => deleteProduct(Number(params.productId)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["productList"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getProductList"],
      });

      navigate(-1);
    },
  });

  const { data: sellerInfo } = useQuery({
    queryKey: ["sellerInfo", productInfo?.seller_id],
    queryFn: () => getSellerInfo(Number(productInfo?.seller_id)),
    enabled: !!productInfo?.seller_id,
  });

  const getProductList = useCallback(async () => {
    let url = `${GET_PRODUCT_URL}?page=${1}&limit=${LOAD_SIZE_10}&seller_id=${productInfo?.seller_id}`;

    const res = await axiosInstance.get(url);
    if (res.status === 200) {
      return res.data.data.list;
    }
  }, [axiosInstance, productInfo?.seller_id]);

  const { data: sellerOtherList } = useQuery({
    queryKey: ["sellerOtherList", productInfo?.seller_id],
    queryFn: getProductList,

    enabled: !!productInfo?.seller_id,
  });

  const filteredSellerOtherList =
    sellerOtherList?.filter(
      (item: any) => item.product_id !== Number(params.productId),
    ) || [];

  const getBrandOtherList = useCallback(async () => {
    let url = `${GET_PRODUCT_URL}?page=${1}&limit=${LOAD_SIZE_10}&brand_id=${productInfo?.brand_id}`;

    const res = await axiosInstance.get(url);
    if (res.status === 200) {
      return res.data.data.list;
    }
  }, [axiosInstance, productInfo?.brand_id]);

  const { data: brandOtherList } = useQuery({
    queryKey: ["brandOtherList", productInfo?.brand_id],
    queryFn: getBrandOtherList,

    enabled: !!productInfo?.brand_id,
  });

  const filteredBrandOtherList =
    brandOtherList?.filter(
      (item: any) => item.product_id !== Number(params.productId),
    ) || [];

  // 장바구니 담기
  const { mutate } = useMutation({
    mutationFn: (cartData: any) => postCart(cartData),
    onSuccess: () => {
      setCartModalOpen(true);
    },
  });

  // 상품 복사하기
  const { mutate: duplicateMutate } = useMutation({
    mutationFn: () => postCopyProduct(Number(params.productId)),
    onSuccess: (data) => {
      setOpenDrawer(false);
      // NOTE: 상품 복사 후 상품 상세 페이지로 이동
      navigate(`/product-detail/${data?.new_product_id}`);
      queryClient.resetQueries({
        queryKey: ["productList"],
      });
    },
  });

  const handleOpenPolicyModal = () => {
    setModal(true);
  };

  const isUserProduct = userInfo?.id === productInfo?.seller_id;

  const handleMenuClick = () => {
    setOpenDrawer(true);
  };

  // 장바구니 담기
  const handleCartClick = (optionList: any) => {
    if (
      optionList.length === 0 ||
      (optionList.length > 0 && optionList[0].size_list.length === 0)
    ) {
      return false;
    }

    const cartData = {
      product_id: Number(params.productId),
      option_list: optionList.map((option: any) => ({
        color_id: option.color_id,
        size_list: option.size_list.map((size: any) => ({
          size_id: size.size_id,
          amount: size.amount,
        })),
      })),
    };

    mutate(cartData);
    return true;
  };
  // 구매하기
  const handlePurchaseClick = (optionList: any) => {
    if (
      optionList.length === 0 ||
      (optionList.length > 0 && optionList[0].size_list.length === 0)
    ) {
      return false;
    }

    // 즉시 구매 데이터
    const productList = optionList.flatMap((option: any) =>
      option.size_list.map((size: any, sizeIndex: number) => ({
        cart_id: Number(
          `${productInfo.product_id}${option.color_id}${size.size_id}000`,
        ),
        amount: size.amount,
        product_condition: productInfo.product_condition,
        brand_name_ko: productInfo.brand_name_ko,
        brand_name_en: productInfo.brand_name_en,
        thumbnail_url: productInfo.product_image_list[0].image_url,
        size_name: size.size_name,
        color_id: option.color_id,
        price: productInfo.price,
        product_id: productInfo.product_id,
        size_id: size.size_id,
        product_name_ko: productInfo.product_name_ko,
        product_name_en: productInfo.product_name_en,
        color_name: option.color_name,
        stock: size.maxAmount,
        seller_id: productInfo.seller_id,
      })),
    );

    const purchaseData = {
      shipping_fee: productInfo.shipping_fee,
      free_shipping_fee: productInfo.free_shipping_fee,
      profile_image: sellerInfo?.profile_image,
      nickname: sellerInfo?.nickname,
      seller_id: sellerInfo?.seller_id,
      product_list: productList,
    };

    setOrder([purchaseData]);
    setOrderType("PURCHASE");
    setPurchaseModalOpen(true);
    return true;
  };

  const handleNavigateSubscribe = () => {
    navigate("/subscribe");
  };

  // 상품 설명 길이에 따라 늘어나는 textarea
  const AutoExpandTextarea = ({ value }: { value: string }) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }, [value]);

    return (
      <textarea
        ref={textareaRef}
        disabled
        value={value}
        style={{
          width: "100%",
          minHeight: "100px", // 최소 높이 유지
          resize: "none",
          overflow: "hidden",
          whiteSpace: "pre-wrap",
        }}
      />
    );
  };

  return productInfoLoading ? (
    <></>
  ) : (
    <Container>
      <BackHeader title="상품 정보" />

      <EditButtonContainer>
        {isUserProduct && (
          <>
            <EditMenuButton>
              <StyledImage
                onClick={handleMenuClick}
                src={theme.image.dot_icon.default}
              />
            </EditMenuButton>
          </>
        )}
        <ProductSwiper imageList={productInfo?.product_image_list} />
      </EditButtonContainer>

      <BasicDrawer
        open={openDrawer}
        setOpen={setOpenDrawer}
        wrapperPadding="0"
        padding="0">
        <ItemBox>
          <Item onClick={() => duplicateMutate()}>{t("상품 복사하기")} </Item>
          <Item onClick={() => setDeactiveModal(true)}>
            {productStatus ? t("상품 비활성화") : t("상품 활성화")}
          </Item>

          <Item
            onClick={() =>
              navigate("/product-edit", {
                state: { productInfo },
              })
            }>
            {t("수정하기")}
          </Item>

          <Item onClick={() => setDeleteModal(true)}>{t("삭제하기")}</Item>
          <Item onClick={() => setOpenDrawer(false)}>{t("취소")}</Item>
        </ItemBox>
      </BasicDrawer>

      <ProductInfoWrapper>
        <CategoryBox>
          <span>
            {lang === "en" && productInfo?.category_en
              ? productInfo?.category_en
              : productInfo?.category_ko}
          </span>

          <IconBox>
            <LikeButton
              isLiked={productInfo?.is_liked === 1}
              id={productInfo?.product_id}
              type="product"
            />
            <ShareButton />
          </IconBox>
        </CategoryBox>

        <BrandWithTitle>
          <button
            className="brand_name"
            onClick={() => navigate(`/brand/${productInfo?.brand_id}`)}>
            <span className="brand">
              {lang === "en"
                ? productInfo?.brand_name_en
                : productInfo?.brand_name_ko}
            </span>
            <Image
              src={theme.image.arrow_right.default}
              width="2rem"
              height="2rem"
            />
          </button>

          <span className="title ellipsis-1">
            {lang === "en"
              ? productInfo?.product_name_en
              : productInfo?.product_name_ko}
          </span>
        </BrandWithTitle>

        <ConditionBox>
          <span className="label">{t("컬러")}</span>
          <div className="color_box">
            {productInfo?.color_list?.map((item: any) => (
              <ColorBall
                color={item.hex_code}
                colorName={item.name}
                key={item.color_id}
                onClick={() => setSelectedColor(item.color_id)}
                active={item.color_id === selectedColor}
              />
            ))}
          </div>
        </ConditionBox>
        <ConditionBox>
          <span className="label">{t("사이즈")}</span>

          <div className="color_box">
            {size?.map((item: any) => (
              <SizeItem
                className="size"
                key={item.id}
                disabled={item.amount === 0}>
                {item.name}
              </SizeItem>
            ))}
          </div>
        </ConditionBox>

        <ConditionBox>
          <span className="label"> {t("컨디션")}</span>
          <span className="size">
            {lang === "en"
              ? productInfo?.product_condition_en
              : productInfo?.product_condition_ko}
          </span>
        </ConditionBox>

        <PriceBox>
          <span>{toPrice(productInfo?.price * 2)}</span>
          <span
            className="discount"
            style={{ display: "flex", gap: "0.6rem", alignItems: "flex-end" }}>
            <span
              style={{
                fontSize: "1.2rem",
              }}>
              {t("회원가")}
            </span>
            {productInfo?.product_condition === "NEW"
              ? toPrice(productInfo?.price)
              : toPrice(Math.round(productInfo?.price * 2))}
          </span>
        </PriceBox>
      </ProductInfoWrapper>

      <AdWrapper>
        <AdSwiper onClick={handleNavigateSubscribe} />

        <Line />
      </AdWrapper>
      <LabelWithContent>
        <label>{t("배송비")}</label>

        {/* <DeliveryFeeBox> */}
        <Flex justify="space-between">
          <Delivery> {t("기본 배송비 (도서산간 추가비용)")}</Delivery>
          <span className="value">
            {toPrice(productInfo?.shipping_fee)} (+
            {toPrice(4000)})
          </span>
        </Flex>
        {/* </DeliveryFeeBox> */}

        <Delivery> {t("상품상세 무료배송")}</Delivery>

        <DeliveryFeeBox>
          <span className="value">
            <Flex vertical gap={"0.5rem"} align="flex-end">
              <span>
                {t("구독 회원 배송비")}{" "}
                {toPrice(productInfo?.free_shipping_fee)} {t("원 이상 구매 시")}
              </span>

              <span>
                {t("비구독회원 배송비")}{" "}
                {toPrice(productInfo?.free_shipping_fee * 2)}{" "}
                {t("원 이상 구매 시")}
              </span>
            </Flex>
          </span>
        </DeliveryFeeBox>

        <Line />
      </LabelWithContent>
      <LabelWithContent>
        <label>{t("상품설명")}</label>

        <ProductDescription>
          <ProductDescription>
            <AutoExpandTextarea
              value={
                lang === "en"
                  ? productInfo?.description_en
                  : productInfo?.description_ko
              }
            />
          </ProductDescription>
        </ProductDescription>

        <Line />
      </LabelWithContent>
      <LabelWithContent>
        <label>
          <span>{t("교환 / 환불 정책")}</span>
          <button
            onClick={handleOpenPolicyModal}
            style={{
              paddingTop: "0.2rem",
            }}>
            <Image
              src={theme.image.warning_circle.default}
              width="1.6rem"
              height="1.6rem"
            />
          </button>
        </label>

        <div
          style={{
            display: "flex",
            gap: "2rem",
          }}>
          <PolicyBox>
            <Image
              src={
                productInfo?.is_exchange_allowed
                  ? theme.image.success_box.default
                  : theme.image.fail_box.default
              }
              width="4.8rem"
              height="4.8rem"
            />

            <span>{t("교환")}</span>
          </PolicyBox>
          <PolicyBox>
            <Image
              src={
                productInfo?.is_refund_allowed
                  ? theme.image.success_box.default
                  : theme.image.fail_box.default
              }
              width="4.8rem"
              height="4.8rem"
            />

            <span>{t("환불")}</span>
          </PolicyBox>
          <PolicyBox>
            <Image
              src={
                productInfo?.is_return_allowed
                  ? theme.image.success_box.default
                  : theme.image.fail_box.default
              }
              width="4.8rem"
              height="4.8rem"
            />

            <span>{t("반품")}</span>
          </PolicyBox>
        </div>
        <Line />
      </LabelWithContent>
      <LabelWithContent>
        <label>
          <span>{t("해당 판매자의 다른 상품")}</span>
        </label>

        <SellerInfoCard button sellerId={productInfo?.seller_id} />

        <ProductListSwiper productList={filteredSellerOtherList} />

        <Line />
      </LabelWithContent>
      <LabelWithContent last>
        <label>
          <span>{t("해당 브랜드의 다른 상품")}</span>
        </label>

        <label onClick={() => navigate(`/brand/${productInfo?.brand_id}`)}>
          <span className="brand">
            {lang === "en"
              ? productInfo?.brand_name_en
              : productInfo?.brand_name_ko}
          </span>

          <button style={{ display: "flex" }}>
            <Image
              src={theme.image.arrow_right.default}
              width="1.6rem"
              height="1.6rem"
            />
          </button>
        </label>

        <ProductListSwiper productList={filteredBrandOtherList} />

        <Line />
      </LabelWithContent>
      <ExchangeRefundModal open={modal} setOpen={setModal} />

      <ChoiceOptionDrawer
        colorList={productInfo?.color_list}
        onCartClick={handleCartClick}
        onPurchaseClick={handlePurchaseClick}
        productPrice={productInfo?.price}
        productCondition={productInfo?.product_condition}
      />

      <BasicModal
        open={cartModalOpen}
        setOpen={setCartModalOpen}
        activeName="확인"
        activeEvent={() => {
          navigate("/cart");
        }}
        closeEvent={() => setCartModalOpen(false)}>
        <ModalContent
          mainMessage="장바구니에 상품을 담았습니다."
          subMessage="장바구니로 이동하시겠습니까?"
        />
      </BasicModal>

      {/* 즉시 구매 */}
      <BasicModal
        open={purchaseModalOpen}
        setOpen={setPurchaseModalOpen}
        activeEvent={() => {
          navigate("/order", {
            state: {
              isDirectPurchase: true,
            },
          });
          setPurchaseModalOpen(false);
        }}
        activeName="확인"
        closeEvent={() => setPurchaseModalOpen(false)}
        closeName="취소">
        <ModalContent mainMessage="선택한 상품을 바로 구매하시겠습니까?" />{" "}
      </BasicModal>

      <BasicModal
        open={deactiveModal}
        setOpen={setDeactiveModal}
        activeEvent={() => {
          updateProductStatusMutate(productStatus ? false : true);
          setDeactiveModal(false);
        }}
        activeName="확인"
        padding="2rem">
        <ModalContent
          mainMessage={
            productStatus
              ? "상품 상태를 비활성화 하시겠습니까?"
              : "상품 상태를 활성화 하시겠습니까?"
          }
        />
      </BasicModal>

      <BasicModal
        open={deleteModal}
        setOpen={setDeleteModal}
        activeEvent={() => {
          deleteProductMutate(productInfo?.id);
          setDeleteModal(false);
        }}
        activeName="삭제하기"
        padding="2rem">
        <ModalContent mainMessage="상품을 삭제하시겠습니까?" />

        <Flex vertical gap="2rem">
          <WarningMessage>
            {t(
              "상품을 삭제하면 연관된 모든 부분에서 삭제되며 되돌릴 수 없습니다.",
            )}
          </WarningMessage>
          <WarningMessage>
            {t("(기획전, WE LOVE, 좋아요, 다른 사용자의 장바구니 등)")}
          </WarningMessage>
        </Flex>
      </BasicModal>
    </Container>
  );
};

export default ProductDetail;
